/* eslint-disable import/no-unresolved */
/* eslint-disable no-param-reassign */
import { useState, useEffect, useRef } from "react";
import { callApi } from "@src/api";

import { useAuth } from "@src/hooks/useAuth";
import moment from "moment";
import { YMD_FORMAT, DAYS_OF_WEEK } from "@src/constants/date";
import {
  each,
  find,
  filter,
  flatten,
  map,
  includes,
  extend,
  sumBy,
  uniq,
  maxBy,
} from "lodash";
import { toast } from "react-toastify";
import UseEmployeeData from "./UseEmployeeData";

const dow = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const defaultCalendarSettings = {
  mode: "manual",
  snap: "00:15",
  slot: "00:30",
  viewDays: 14,
  syncDays: 28,
  defaultStartTime: "10:00",
  defaultDuration: "09:00",
  storeHours: {
    start: "07:00",
    end: "22:00",
  },
};

const useScheduleData = () => {
  const [fetchLoading, setFetchLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [calendarSettings, setCalendarSettings] = useState(
    defaultCalendarSettings
  );
  const firstRenderRef = useRef(false);
  const [draftAvailable, setDraftAvailable] = useState(false);
  const [refetch, setRefetch] = useState(false);

  const [allData, setAllData] = useState([]);
  const [allTemplateData, setAllTemplateData] = useState([]);
  const [periodicSchedule, setPeriodicSchedule] = useState([]);

  const [selectedTemplate, setSelectedTemplate] = useState(0);

  const [hasUnsavedDraft, setHasUnsavedDraft] = useState(false);

  const handleTemplateChange = (x) => {
    setAllData(
      allData.map((el) => ({
        ...el,
        staticschedules: allTemplateData.filter(
          (data) => data.employee_id === el.id && data.template_id === x
        ),
      }))
    );
  };

  const [mode, setMode] = useState("publish");

  // const modePublished = true;
  const [nDays, setNDays] = useState(calendarSettings.viewDays);
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );

  const [period, setPeriod] = useState([
    moment().format("YYYY-MM-DD"),
    moment()
      .add(calendarSettings.viewDays - 1, "days")
      .format("YYYY-MM-DD"),
  ]);

  const dMD = "ddd, MMM DD";
  // const hma = "hh:mm A";

  // const startDur = moment.duration(calendarSettings.storeHours.start);
  const endDur = moment.duration(calendarSettings.storeHours.end);

  // const dayStartHr = startDur.hours();
  // const dayStartMin = startDur.minutes();
  const dayEndHr = endDur.hours();
  const dayEndMin = endDur.minutes();

  const {
    selectedBusiness,
    updateCurrentBusiness,
    // business,
  } = useAuth();

  const id = selectedBusiness?.id;

  // getEmployeeData
  const { employeeData, getEmployeeData, updateEmployeeAppointmentVisibility } =
    UseEmployeeData();

  const [publishedEvents, setPublishedEvents] = useState([]);

  const getPastSchedule = async ({ start, end }) => {
    setFetchLoading(true);
    const res = await callApi(
      `/protected/business/${id}/staff/schedules`,
      "GET",
      null,
      null,
      { start, end }
    );
    if (res?.status) {
      setPeriodicSchedule(
        res.data?.flatMap((x) => x).filter((x) => !x.archived_at)
      );
    }
    setFetchLoading(false);
    return res;
  };

  // const past =
  //   moment(selectedDate).format("YYYY-MM-DD") < moment().format("YYYY-MM-DD");

  // to get the dynamic schedule data
  const getDynamicSchedules = async () => {
    setFetchLoading(true);
    const params = {
      start: moment(selectedDate).format(YMD_FORMAT),
      end: moment(selectedDate)
        .add(calendarSettings.viewDays - 1, "days")
        .format(YMD_FORMAT),
    };

    const response = await callApi(
      `/protected/business/${id}/schedule/get/dynamic`,
      "get",
      null,
      null,
      { start: params.start }
    );
    setFetchLoading(false);

    if (response?.status) {
      setAllTemplateData(flatten(map(response.data, "staticschedules")) || []);

      setAllData(
        response.data?.map((el) => ({
          ...el,
          staticschedules: el.staticschedules.filter(
            (data) => data.template_id === selectedTemplate
          ),
        }))
      );
    }
    return response;
  };

  const getStaffList = () => {
    const staff = employeeData;
    const defaultSchedules = [];
    each(staff, (emp) => {
      if (emp.status === "ACTIVE") {
        defaultSchedules.push({
          active: false,
          visible: true,
          start: calendarSettings.defaultStartTime,
          duration: calendarSettings.defaultDuration,
          employeeId: emp.id,
          title: emp.name || emp.nickname || emp.full_name,
          color: emp.attributes && emp.attributes.schedulerColor,
          attributes: emp.attributes,
          temp: true,
          stick: true,
        });
      }
    });
    return defaultSchedules;
  };

  const defaultSchedules = getStaffList();

  const getResourceIdForEvent = (sched) =>
    `${sched.day || DAYS_OF_WEEK[sched.dow]}-${sched.employee_id}`;

  const getGroupTitleForEvent = (sched) =>
    `${
      (sched.day && moment(sched.day).format(dMD)) || DAYS_OF_WEEK[sched.dow]
    }`;

  const getInactive = (eid) => {
    const emp = find(employeeData, { id: eid });
    const isActive = emp && emp.status === "ACTIVE";
    return emp
      ? (emp.nickname || emp.full_name) + (isActive ? "" : "(INACTIVE)")
      : "Unknown Staff";
  };

  const getTitle = (staff, sched) =>
    staff ? staff.title : getInactive(sched.employee_id);

  const getEvent = (sched, isFuture) => {
    const offset =
      moment()
        .startOf("day")
        .add(1, "day")
        .diff(moment().startOf("day"), "hours") - 24;
    const day = moment().startOf("day").add(offset, "hours");
    const resId = sched.day || sched.dow;
    const start = moment(day).add(moment.duration(sched.start_time));
    const end = moment(day).add(moment.duration(sched.end_time)).isBefore(start)
      ? moment(day).add(dayEndHr, "hours").add(dayEndMin, "minutes")
      : day.add(moment.duration(sched.end_time));
    const staff = find(defaultSchedules, {
      employeeId: sched.employee_id,
    });

    return {
      id: String(sched.id),
      resourceId: getResourceIdForEvent(sched),
      groupTitle: getGroupTitleForEvent(sched),
      color:
        mode === "publish" && !isFuture
          ? "#666"
          : staff
          ? staff.color || "#03a9f4"
          : "#666",
      title: getTitle(staff, sched),
      start: start.toDate(),
      end:
        end <= start ? moment(start).add(5, "minutes").toDate() : end.toDate(),
      editable: mode !== "publish",
      overlap: false,
      extendedProps: {
        startMoment: start,
        endMoment: end,
        startHm: start.format("HH:mm"),
        endHm: end.format("HH:mm"),
        resId,
        published: sched.published,
        past: sched.past,
        dynamic: sched.day && !sched.dow,
        employeeId: sched.employee_id,
        conflicts: sched.conflicts,
        dow: sched.dow,
        day: sched.day,
        groupId: sched.groupId,
        detail: sched.detail,
      },
    };
  };

  const getEventSourceIdForEvent = (ev) => {
    const status =
      ev.published || (ev.extendedProps && ev.extendedProps.published)
        ? "published"
        : "draft";
    const staff =
      ev.employeeId || (ev.extendedProps && ev.extendedProps.employeeId) || "0";
    const evMode = ev.day ? "dynamic" : "dynamic";
    const dynMode = status === "published" ? evMode : "dynamic";
    return `${dynMode + status}staff${staff}`;
  };

  const createNewResource = (ev, Id) => ({
    id: Id,
    published: ev.extendedProps.published,
    past: ev.extendedProps.past,
    dynamic: ev.extendedProps.dynamic,
    employeeId: ev.extendedProps.employeeId,
    events: [],
  });

  // const isFutureDate = (x) =>
  //   x ? moment(x, YMD_FORMAT).startOf("day").isAfter(now) : false;

  const getEventsFromSchedule = (schedules) => {
    const now = moment().startOf("day").subtract(1, "minute");
    const dynamic = mode !== "template";
    const sources = {};
    const eventSources = [];
    const events = [];

    each(schedules, (sched) => {
      const isFuture = sched.day
        ? moment(sched.day, YMD_FORMAT).startOf("day").isAfter(now)
        : true;

      if (
        (dynamic &&
          sched.day &&
          !sched.dow &&
          (sched.published || !isFuture || (isFuture && mode === "edit"))) ||
        (!dynamic && sched.dow >= 0 && !sched.day)
      ) {
        events.push(getEvent(sched, isFuture));
      }
    });
    each(events, (ev) => {
      const eventID = getEventSourceIdForEvent(ev);
      if (!sources[eventID]) {
        sources[eventID] = createNewResource(ev, eventID);
      }
      sources[eventID].events.push(ev);
    });
    each(sources, (value) => {
      eventSources.push(value);
    });
    return eventSources;
  };

  const staticSchedules = filter(
    flatten(map(allData, "staticschedules")),
    (s) =>
      s &&
      s.employee_id &&
      s.published &&
      !!find(defaultSchedules || [], {
        employeeId: s.employee_id,
      })
  );

  const dynamicSchedules = filter(
    flatten(map(allData, "dynamicschedules")),
    (s) =>
      s &&
      s.employee_id &&
      !!find(defaultSchedules || [], {
        employeeId: s.employee_id,
      })
  );

  const allSchedules = getEventsFromSchedule(
    mode !== "template"
      ? mode === "publish"
        ? periodicSchedule
        : dynamicSchedules
      : staticSchedules
  );

  const getEventSourceIdForStaff = (staff, pub) => {
    const status = pub ? "published" : "draft";
    const dynMode = status === "published" ? "dynamic" : "dynamic";
    return `${dynMode + status}staff${staff}`;
  };

  const defaultSchedulesWithHours = each(defaultSchedules, (staff) => {
    const pub = mode !== "edit";
    const sid = getEventSourceIdForStaff(staff.employeeId, pub);
    const sched = find(allSchedules, {
      id: sid,
    });
    const dur = sched
      ? sumBy(sched.events, (ev) =>
          moment(ev.end).diff(moment(ev.start), "minutes")
        )
      : 0;
    const hr = dur ? (dur / 60).toFixed(2) : 0;
    const days = sched ? uniq(sched.events, "resourceId") : [];
    const ds = "D/";
    const str = days.length ? `${days.length + ds + hr}Hrs` : "";
    staff.thrs = dur;
    staff.tdays = days.length;
    staff.totalHours = str;
  });

  const conflictingSchedules = map(
    filter(allSchedules, (item) => item.conflicts),
    (item) =>
      `${mode !== "publish" ? item.day : dow[item.dow]}-${item.employee_id}`
  );

  const getViewCalResources = () => {
    const calResources = [];
    // let conflictingSchedules = [];
    let i;
    let resObj;
    const day = mode === "publish" ? moment(period[0]) : moment(selectedDate);
    const nDaysTemp =
      mode === "publish"
        ? moment(period[1]).diff(moment(period[0]), "days") + 1
        : nDays;
    const activeStaff = filter(employeeData, (e) => e.status === "ACTIVE");
    const addCalResource = (resObjData) => {
      each(activeStaff, (e) => {
        const name = e.full_name + (e.nickname ? `\n${e.nickname}` : "");
        const eID = `${resObjData.groupId}-${e.id}`;
        const conflicts = includes(conflictingSchedules, eID);
        calResources.push(
          extend(
            {
              id: eID,
              conflicts,
              title: name,
              employeeId: e.id,
            },
            resObjData
          )
        );
      });
    };
    for (i = 0; i < nDaysTemp; i += 1) {
      resObj = {
        template: false,
        groupId: day.format(YMD_FORMAT),
        m: moment(day),
        groupTitle: day.format(dMD),
        dow: day.day(),
      };
      addCalResource(resObj);
      day.add(1, "day");
    }
    for (i = 0; i < 7; i += 1) {
      resObj = {
        template: true,
        day: day.format(YMD_FORMAT),
        groupId: DAYS_OF_WEEK[i],
        m: moment(day),
        groupTitle: DAYS_OF_WEEK[i],
        dow: i,
      };
      addCalResource(resObj);
    }

    return filter(calResources, {
      template: mode === "template",
    });
  };

  const isSourceVisible = (src) => {
    const res = find(
      defaultSchedules,
      (item) => item.employeeId === src.employeeId
    );
    if (res) {
      return res.visible;
    }
    return true;
  };

  const showPublished = () => {
    const staticSchedule = filter(
      flatten(map(allData, "staticschedules")),
      (s) =>
        s &&
        s.employee_id &&
        s.published &&
        !!find(defaultSchedules || [], {
          employeeId: s.employee_id,
        })
    );

    const dynamicSchedule = filter(
      flatten(map(allData, "dynamicschedules")),
      (s) =>
        s &&
        s.employee_id &&
        !!find(defaultSchedules || [], {
          employeeId: s.employee_id,
        })
    );

    if (dynamicSchedule?.filter((el) => !el.published)?.length)
      setDraftAvailable(true);
    const allSchedule = getEventsFromSchedule(
      mode !== "template"
        ? mode === "publish"
          ? periodicSchedule
          : dynamicSchedules
        : staticSchedule
    );
    const source = [];
    const resources = map(getViewCalResources(), "employeeId");

    const p = filter(
      allSchedule,
      (item) =>
        (mode === "edit" ? !item.published : item.published || item.past) &&
        !!find(
          item.events,
          (e) =>
            (mode === "edit"
              ? true
              : e.extendedProps.published || e.extendedProps.past) &&
            includes(resources, e.extendedProps.employeeId)
        )
    );

    each(p, (item) => {
      if (isSourceVisible(item)) {
        source.push(item);
      }
    });
    return source;
  };

  const getResources = () => {
    const viewCalResources = getViewCalResources();
    const visibleStaff = map(
      filter(defaultSchedules, (emp) => emp.visible),
      "employeeId"
    );

    // const emp = filter(viewCalResources |st| [], (res) => includes(visibleStaff, res.employeeId));
    return filter(viewCalResources || [], (res) =>
      includes(visibleStaff, res.employeeId)
    );
  };

  // const removeSource = (idOrArr) => {
  //     let sources = idOrArr ? (isArray(idOrArr) ? idOrArr : [idOrArr]) : null;
  //     if (viewcalendar) {
  //         viewcalendar.batchRendering(() => {
  //             if (!isEmpty(sources)) {
  //                 each(sources, (src) => {
  //                     const source = calendarMethod('getEventSourceById', src);
  //                     if (source) {
  //                         source.remove();
  //                     }
  //                 });
  //             }
  //             else {
  //                 sources = calendarMethod('getEventSources');
  //                 each(sources, (source) => {
  //                     source.remove();
  //                 });
  //             }
  //         });
  //     }
  //   }

  // const updateEventSources = () => {
  //   removeSource();
  //   if (editMode) {
  //       showDraft();
  //   } else {
  //       showPublished();
  //   }
  //   updateStaffHours();
  //   calendarMethod('refetchResources')
  // }

  // const Schedule = function (obj) {
  //   const schedule = obj || {};
  //     const st = moment(schedule.start);
  //     const en = moment(schedule.end);
  //     const startHour = st.hour() || 0;
  //     const startMin = st.minute() || 0;
  //     const endHour = en.hour() || 0;
  //     const endMin = en.minute() || 0;
  //     const r =
  //       (schedule && schedule.resource && schedule.resource.toJSON()) ||
  //       (schedule && schedule.getResources && schedule.getResources()[0]);
  //     const res =
  //       r ||
  //       find( getViewCalResources(), {
  //         employeeId: schedule.extendedProps.employeeId,
  //       });
  //     const nth = function () {};
  //     const that = this;
  //     const hasChanged = function () {
  //       let same = false;
  //         const resId =
  //           mode === "publish"
  //             ? schedule.extendedProps.resId
  //             : DAYS_OF_WEEK[schedule.extendedProps.resId];
  //       if (schedule && schedule.id) {
  //         same =
  //           schedule.extendedProps.startHm === that.startHm &&
  //           schedule.extendedProps.endHm === that.endHm &&
  //           resId === that.schedDay;
  //         return !same;
  //       }
  //       return true;
  //     };
  //     const update = function () {
  //       const stHr = that.start ? that.start.hour() : startHour;
  //         const stMin = that.start ? that.start.minute() : startMin;
  //         const endHr = that.end ? that.end.hour() : endHour;
  //         const endM = that.end ? that.end.minute() : endMin;
  //         const setStartHr = stHr < dayStartHr ? dayStartHr : stHr;
  //         const setStartMin =
  //           stHr < dayStartHr
  //             ? dayStartMin
  //             : stHr > dayStartHr
  //             ? stMin
  //             : stMin > dayStartMin
  //             ? stMin
  //             : dayStartMin;
  //         const setEndHr = endHr <= dayEndHr ? endHr : dayEndHr;
  //         const setEndMin =
  //           endHr > dayEndHr
  //             ? dayEndMin
  //             : endHr < dayEndHr
  //             ? endM
  //             : endM <= dayEndMin
  //             ? endM
  //             : dayEndMin;

  //       that.start = moment(res.extendedProps.m)
  //         .hour(setStartHr)
  //         .minute(setStartMin);
  //       that.end = moment(res.extendedProps.m).hour(setEndHr).minute(setEndMin);
  //       that.startHm = that.start.format("HH:mm");
  //       that.endHm = that.end.format("HH:mm");
  //       that.duration = moment(that.end).diff(that.start, "minutes");
  //       that.durHours = parseInt(that.duration / 60, 10);
  //       that.durMinutes = parseInt(that.duration % 60, 10);
  //       that.startTime = that.start.format(hma);
  //       that.endTime = that.end.format(hma);
  //       that.day = mode === "publish" ? that.start.format(dMD) : null;
  //       that.schedDay =
  //         mode === "publish"
  //           ? that.start.format("YYYY-MM-DD")
  //           : DAYS_OF_WEEK[res.extendedProps.dow];
  //       that.dayPretty = res.extendedProps && res.extendedProps.groupTitle;
  //       that.dow = mode === "publish" ? null : res.extendedProps.dow;
  //       that.title = schedule.title;
  //       that.employeeId = schedule.id
  //         ? res && res.extendedProps.employeeId
  //         : schedule.extendedProps.employeeId;
  //       that.published =
  //         schedule.extendedProps && schedule.extendedProps.published;
  //       that.editable = mode === "edit";
  //       that.hasChanged = hasChanged();
  //       that.schedType =
  //         mode === "edit"
  //           ? "Draft"
  //           : mode === "published"
  //           ? "Schedule"
  //           : "Template";
  //       that.conflicts = schedule.extendedProps.conflicts;
  //       that.temp = schedule.extendedProps.temp;
  //     };
  //     const onError = function (data) {
  //       if (that.revertFn) {
  //         that.revertFn();
  //       }
  //       alert.toast(data.message || "Oops! Error Occured!", "error", 3000);
  //       // $scope.alert.alert(data.message || "An error occured. Draft not saved.");
  //     };
  //     const deleteLocal = function () {
  //       if (schedule) {
  //         schedule.remove();
  //       }
  //       const resEvent = find(allSchedules, {
  //           id: getEventSourceIdForEvent(that),
  //         });
  //         let d;
  //       if (resEvent) {
  //         remove(resEvent.events, (item) => (
  //             (item.id && item.id === that.id) ||
  //             (item.reference && that.reference === item.reference)
  //           ));

  //         if (mode === "edit") {
  //           d = filter(allSchedules, (item) => !item.published);
  //           // $scope.hasDraft = !!d.length;
  //         }
  //         each(
  //           filter(calendarMethod("getEvents"), (ev) => {
  //             const ref =
  //               ev.reference ||
  //               (ev.extendedProps && ev.extendedProps.reference);
  //             return (
  //               (that.id && ev.id === that.id) ||
  //               (that.reference && ref === that.reference)
  //             );
  //           }),
  //           (ev) => {
  //             ev.remove();
  //           }
  //         );
  //         updateEventSources();
  //       }
  //     };

  //   if (res && !schedule.resource && !(schedule && schedule.getResources)) {
  //     res.extendedProps = {
  //       m: res.m,
  //       groupTitle: res.groupTitle,
  //       employeeId: res.employeeId,
  //       dow: res.dow,
  //     };
  //   }
  //   extend(that, {
  //     remove () {
  //       if (that.id) {
  //         return deleteThisEvent(
  //           that.id,
  //           (data) => {
  //             if (!(data && data.status)) {
  //               alert.toast("Oops, Failed to Delete!", "error", 3000);
  //               return onError(data);
  //             }
  //             alert.toast("Schedule Deleted!", "success", 2000);
  //             deleteLocal();
  //           },
  //           onError
  //         );
  //       }
  //       deleteLocal();
  //     },
  //     update (revertFn) {
  //       that.revertFn = revertFn || nth;
  //       update();
  //       if (hasChanged()) {
  //         const evObj = getEventForUpdate(that);
  //         return updateThisEvent(
  //           that.id,
  //           evObj,
  //           (data) => {
  //             if (!(data && data.status)) {
  //               alert("Oops, Update Failed!", "error", 3000);
  //               return onError(data);
  //             }
  //             alert(`${that.schedType  } Updated!`, "success", 2000);
  //             evObj.id = data.data.id;
  //             evObj.published = that.published;
  //             updateEventData(evObj);
  //           },
  //           onError
  //         );
  //       }
  //       return revertFn && revertFn();
  //     },
  //     changeStartTime (fall) {
  //       if (fall) {
  //         that.start.subtract(moment.duration(calendarSettings.snap));
  //       } else {
  //         const newStart = moment(that.start).add(
  //           moment.duration(calendarSettings.snap)
  //         );
  //         if (newStart.isBefore(that.end)) {
  //           that.start = newStart;
  //         }
  //       }
  //       update();
  //     },
  //     changeEndTime (fall) {
  //       if (fall) {
  //         const newEnd = moment(that.end).subtract(
  //           moment.duration(calendarSettings.snap)
  //         );
  //         if (newEnd.isAfter(that.start)) {
  //           that.end = newEnd;
  //         }
  //       } else {
  //         that.end.add(moment.duration(calendarSettings.snap));
  //       }
  //       update();
  //     },
  //     save (revertFn) {
  //       if (that.id) {
  //         return that.update(evObj);
  //       }
  //       that.revertFn = revertFn || nth;
  //       update();
  //       if (hasChanged() && !that.id && that.startHm && that.endHm) {
  //         var evObj = getEventForUpdate(that);
  //         if (evObj) {
  //           return updateThisEvent(
  //             that.id,
  //             evObj,
  //             (data) => {
  //               if (data && data.status) {
  //                 alert.toast(`${that.schedType  } Saved!`, "success", 2000);
  //                 merge(evObj, data.data);
  //                 removeTemporaryEvent();
  //                 return addEventToDrafts(evObj);
  //               }
  //               alert.toast("Oops, Save Failed!", "error", 3000);
  //               onError(data);
  //             },
  //             onError
  //           );
  //         }
  //       }
  //       return revertFn && revertFn();
  //     },
  //   });

  //   each(schedule, (v, k) => {
  //     if (!isUndefined(schedule[k])) {
  //       that[k] = schedule[k];
  //     }
  //   });
  //   that.id = schedule.id;
  //   that._ev = schedule;
  //   update();
  //   return that;
  // };

  // const publishedEvents = showPublished();
  const resources = getResources();

  const deleteEvent = async (eventId) => {
    if (!eventId) return;
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/delete/${eventId}/${
        mode === "template" ? "static" : "dynamic"
      }`,
      "POST"
    );
    if (response.status) {
      if (mode === "edit") setHasUnsavedDraft(true);
      getDynamicSchedules();
    } else toast.error("Failed to delete schedule");
  };

  const checkAlternateDaysOverlap = (start1, start2) => {
    if (!start1 || !start2) return false;
    const a = moment(moment(start1).format("YYYY-MM-DD"));
    const b = moment(moment(start2).format("YYYY-MM-DD"));
    const diff = a.diff(b, "days", false);
    if (diff % 7 === 0 && (diff / 7) % 2 === 0) {
      return true;
    }
    return false;
  };

  const checkOverlap = (eventData, update) => {
    const eligibleEvents = publishedEvents
      .find((el) => el?.employeeId === eventData?.employee_id)
      ?.events?.filter((el) =>
        mode === "edit"
          ? el?.extendedProps?.day === eventData?.day
          : el?.extendedProps?.dow === eventData?.dow
      )
      ?.map((el) => ({
        id: el?.id,
        start_time: el?.extendedProps?.startHm,
        end_time: el?.extendedProps?.endHm,
        detail: el?.extendedProps?.detail,
      }));
    if (!eligibleEvents?.length) return false;

    return !!eligibleEvents.find(
      (el) =>
        (!update || el.id !== eventData.id) &&
        el.end_time > eventData.start_time &&
        eventData.end_time > el.start_time &&
        (!el.detail?.alternate ||
          !eventData?.detail?.alternate ||
          checkAlternateDaysOverlap(
            eventData?.detail?.start,
            el?.detail?.start
          ))
    );
  };

  const addNewEvent = async (event) => {
    const timeFormat = "HH:mm";
    const addEventApi = `/protected/business/${selectedBusiness.id}/schedule/add`;
    const updateEventApi = `/protected/business/${selectedBusiness.id}/schedule/update/${event.id}/static`;
    const updateEventApiDynamic = `/protected/business/${selectedBusiness.id}/schedule/update/${event.id}/dynamic`;

    const eventData = {
      start_time:
        event.start && moment(event.start).isValid()
          ? moment(event.start).format(timeFormat)
          : "09:00",
      end_time:
        event.end && moment(event.end).isValid()
          ? moment(event.end).format(timeFormat)
          : moment("09:00", timeFormat).add(8, "hours").format(timeFormat),
      day: event.extendedProps.groupId || event.day,
      dow:
        event.extendedProps.dow === 0
          ? 0
          : event.extendedProps.dow || event.dow,
      employee_id: event.employeeId || event.extendedProps.employeeId,
      auto: 0,
      detail: event.detail,
    };
    if (
      !moment(eventData.start_time, timeFormat).isValid() ||
      !moment(eventData.end_time, timeFormat).isValid()
    )
      return toast.error("Invalid Date");
    if (checkOverlap({ ...eventData, id: event.id }, event.update)) {
      toast.error("Cannot Set Overlapping Schedules");
      return { status: false };
    }
    if (mode === "template") {
      const response = await callApi(
        event.update ? updateEventApi : addEventApi,
        "POST",
        {
          start_time: `${eventData.start_time}`,
          end_time: `${eventData.end_time}`,
          day: null,
          dow: eventData.dow,
          employee_id: eventData.employee_id,
          auto: 0,
          template_id: selectedTemplate,
          detail: event.detail,
        }
      );
      if (response?.status) await getDynamicSchedules();
      else toast.error("Failed to update data");
      return response;
    }

    const response = await callApi(
      event.update ? updateEventApiDynamic : addEventApi,
      "POST",
      {
        start_time: `${eventData.start_time}`,
        end_time: `${eventData.end_time}`,
        day: eventData.day,
        dow: null,
        employee_id: eventData.employee_id,
        auto: 0,
      }
    );
    if (response.status) {
      setHasUnsavedDraft(true);
      await getDynamicSchedules();
    }
    return response;
  };

  const getEffectiveTemplate = (templates, day) => {
    if (!templates || !templates.length) return 0;
    const dateToday = day || moment().format("YYYY-MM-DD");

    // eslint-disable-next-line no-restricted-syntax
    for (const el of templates) {
      if (el.start) {
        const startDate = moment(el.start);
        const todayDate = moment(dateToday);
        if (startDate.diff(todayDate, "days") <= 0) {
          if (!el.end) return el.id;

          const endDate = moment(el.end);
          if (endDate.diff(todayDate, "days") >= 0) {
            return el.id;
          }
        }
      }
    }
    return 0;
  };

  const checkValidity = (day, detail, templateId) => {
    //  handle template versions
    const effectiveTemplateId = calendarSettings?.multipleTemplate
      ? getEffectiveTemplate(calendarSettings?.templates, day)
      : 0;
    if (templateId !== effectiveTemplateId) return false;

    if (!detail) return true;

    //  handle exception
    if (detail.except && detail.except.length) {
      const date = moment(day).date();
      const quo = Math.ceil(date / 7);
      if (detail.except.includes(quo)) return false;
      if (detail.except.includes(5)) {
        const lastDate = moment(day).endOf("month").date();
        if (lastDate - date < 7) return false;
      }
    }

    //  handle Alternate Weeks
    if (detail.alternate === 1 && detail.start) {
      const date1 = moment(day);
      const date2 = moment(detail.start);
      const diff = date2.diff(date1, "days");
      if (diff > 0 || diff % 2 !== 0) return false;
    }

    return true;
  };

  // convert dow to day

  const dowToDay = (dayOfWeek, week) => {
    const date = moment();
    const day = date.day();
    if (dayOfWeek >= day) {
      return date.add(dayOfWeek - day + 7 * week, "day").format("YYYY-MM-DD");
    } 
      return date.add(7 + dayOfWeek - day + 7 * week, "day").format("YYYY-MM-DD");
    
  };

  const publishSchedules = async (noOfWeeks = 4, remainder) => {
    if (!allTemplateData) {
      toast.error("Couldn't Publish Schedules");
      return null;
    }
    const temp = allTemplateData;
    let data = [];
    for (let i = 0; i < noOfWeeks; i += 1) {
      data.push(
        temp
          .filter((el) =>
            checkValidity(dowToDay(el.dow, i), el.detail, el.template_id)
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, i),
          }))
      );
    }
    if (remainder) {
      const dayOfWeek = moment().day();
      const eligible = [];
      for (let i = 0; i < remainder; i += 1) {
        eligible.push((dayOfWeek + i) % 7);
      }
      data.push(
        temp
          .filter((el) => eligible?.includes(el.dow))
          .filter((el) =>
            checkValidity(
              dowToDay(el.dow, noOfWeeks),
              el.detail,
              el.template_id
            )
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, noOfWeeks),
          }))
      );
    }
    data = flatten(data);
    const res = await callApi(
      `/protected/business/${id}/schedule/clear/republish`,
      "POST",
      data
    );
    if (res && res.status) {
      toast.success(
        `Schedule published for the next ${
          remainder
            ? `${noOfWeeks * 7 + remainder} days`
            : `${noOfWeeks === 1 ? "week" : `${noOfWeeks} weeks`}`
        }`
      );
      await getDynamicSchedules();
      setMode("publish");
      return res.data;
    }
    return null;
  };

  const clearDraft = async (data) => {
    const clearDraftApi = `/protected/business/${selectedBusiness.id}/schedule/clear/drafts`;
    let drafts = flatten(map(allData, "dynamicschedules"));
    if (!data?.all) {
      drafts = drafts.filter((el) => data?.includes(el.employee_id));
    }
    drafts = drafts.filter((el) => !el.published).map((el) => `${el.id}`);
    await callApi(clearDraftApi, "POST", drafts);
    await getDynamicSchedules();
    setDraftAvailable(false);
    toast.success("Drafts cleared successfully");
  };

  const clearDraftAndCopyFromTemplate = async () => {
    if (!allTemplateData) {
      toast.error("Template Not Found");
      return;
    }

    //  TBD
    const temp = allTemplateData;
    let batches = [];
    const offset = moment(selectedDate).day();
    for (let i = 0; i < calendarSettings.viewDays; i += 1) {
      const newDay = moment(selectedDate).add(i, "days").format("YYYY-MM-DD");
      const newDow = (offset + i) % 7;
      batches.push(
        temp
          .filter(
            (el) =>
              el.dow === newDow &&
              checkValidity(newDay, el.detail, el.template_id)
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: newDay,
          }))
      );
    }

    batches = flatten(batches);

    setFetchLoading(true);

    const addBatchApi = `/protected/business/${selectedBusiness.id}/schedule/addbatch`;

    const clearDraftApi = `/protected/business/${selectedBusiness.id}/schedule/clear/drafts`;
    const drafts = flatten(map(allData, "dynamicschedules"))
      .filter((el) => !el.published)
      .map((el) => `${el.id}`);
    await callApi(clearDraftApi, "POST", drafts);

    setHasUnsavedDraft(false);
    const res = await callApi(addBatchApi, "POST", batches);

    await getDynamicSchedules();
    setFetchLoading(false);
    if (res?.status) toast.success("Drafts copied from template");
  };

  const clearTemplate = async (data) => {
    const clearTemplateApi = `/protected/business/${selectedBusiness.id}/schedule/clear/templates`;
    const response = await callApi(clearTemplateApi, "POST", {
      template_id: selectedTemplate,
      eid : (data?.all ? undefined : data)
    });
    if (response?.status) {
      getDynamicSchedules();
      toast.success("Master Template cleared successfully");
    } else toast.error("Failed to clear template");
  };

  const addBatch = async (startDate) => {
    let data;

    if (
      startDate ||
      moment(selectedDate).format("YYYY-MM-DD") !==
        moment().format("YYYY-MM-DD")
    ) {
      const resp = await getDynamicSchedules();
      if (!resp?.status) {
        return toast.error("Unknown Error occurred!");
      }
      data = resp.data || [];
    } else {
      data = allData;
    }
    setFetchLoading(true);

    const addBatchApi = `/protected/business/${selectedBusiness.id}/schedule/addbatch`;
    const batches = flatten(map(data, "dynamicschedules"))
      .filter((el) => el.published)
      .map((el) => ({
        start_time: el.start_time,
        end_time: el.end_time,
        day: el.day,
        dow: null,
        auto: 0,
        employee_id: el.employee_id,
      }));
    const clearDraftApi = `/protected/business/${selectedBusiness.id}/schedule/clear/drafts`;
    const drafts = flatten(map(data, "dynamicschedules"))
      .filter((el) => !el.published)
      .map((el) => `${el.id}`);
    await callApi(clearDraftApi, "POST", drafts);
    setHasUnsavedDraft(false);
    const response = await callApi(addBatchApi, "POST", batches);
    // if(response.status) toast.success("Drafts added from Published Schedule")
    if (response?.status) await getDynamicSchedules();
    setFetchLoading(false);
    return { status: true };
  };

  const publishDraft = async () => {
    const publishEventApi = `/protected/business/${selectedBusiness.id}/schedule/publish`;
    const drafts = flatten(map(allData, "dynamicschedules"))
      .filter((el) => !el.published)
      .map((el) => `${el.id}`);
    await callApi(publishEventApi, "POST", {
      drafts,
      start: moment(selectedDate).format("YYYY-MM-DD"),
      end: moment(selectedDate)
        .add(calendarSettings.viewDays - 1, "days")
        .format("YYYY-MM-DD"),
    });
    await getDynamicSchedules();
    setMode("publish");
    toast.success("Drafts published to schedules");
  };

  const deleteStaffSchedule = async (employeeId, start) => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/clearall/${employeeId}`,
      "POST",
      {
        start: start || `${moment().format("YYYY-MM-DD")}`,
        published: mode === "publish",
      }
    );
    if (response.status) {
      if (mode === "edit") setHasUnsavedDraft(true);
      await getDynamicSchedules();
    }
  };

  const republishStaffSchedule = async (
    employeeId,
    noOfWeeks = 4,
    remainder
  ) => {
    if (!allTemplateData) {
      toast.error("Couldn't Publish Schedules");
      return;
    }
    const temp = allTemplateData?.filter((el) => employeeId === el.employee_id);
    let data = [];
    for (let i = 0; i < noOfWeeks; i += 1) {
      data.push(
        temp
          .filter((el) =>
            checkValidity(dowToDay(el.dow, i), el.detail, el.template_id)
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, i),
          }))
      );
    }
    if (remainder) {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const eligible = [];
      for (let i = 0; i < remainder; i += 1) {
        eligible.push((dayOfWeek + i) % 7);
      }
      data.push(
        temp
          .filter((el) => eligible?.includes(el.dow))
          .filter((el) =>
            checkValidity(
              dowToDay(el.dow, noOfWeeks),
              el.detail,
              el.template_id
            )
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, noOfWeeks),
          }))
      );
    }
    data = flatten(data);
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/clear/republishfor/${employeeId}`,
      "POST",
      data
    );
    if (response.status) {
      toast.success(
        `Staff Schedule published for the next ${
          remainder
            ? `${noOfWeeks * 7 + remainder} days`
            : `${noOfWeeks === 1 ? "week" : `${noOfWeeks} weeks`}`
        }`
      );
      await getDynamicSchedules();
    }
  };

  const assignStaffColor = async (employeeId, color) => {
    const attributes = find(defaultSchedules, { employeeId })?.attributes;
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/staff/${employeeId}/property/update`,
      "POST",
      {
        id: employeeId,
        key: "attributes",
        value: {
          ...attributes,
          schedulerColor: color,
        },
      }
    );
    if (response.status) {
      toast.success("Staff Color assigned");
    }
    await getEmployeeData();
  };

  const getHistory = async () => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/activity/schedule`,
      "GET"
    );
    return response;
  };

  const updateHolidays = async (holiday) => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/holidays/update`,
      "POST",
      { holidays: holiday }
    );
    return response;
  };

  const fetchSettings = async () => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/holidays/fetch`,
      "GET"
    );
    if (response && response.data?.settings) {
      setCalendarSettings({
        ...defaultCalendarSettings,
        ...response.data.settings,
      });
      updateCurrentBusiness({
        ...selectedBusiness,
        info: {
          ...selectedBusiness.info,
          calendarSettings: response.data.settings,
        },
      });
    } else {
      setCalendarSettings({ ...defaultCalendarSettings });
    }
    if (response && response.data?.holidays) {
      setHolidays(response.data.holidays);
    }
  };

  const updateSettings = async (settings) => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/settings`,
      "POST",
      settings
    );
    await fetchSettings();
    return response;
  };

  const updateTemplates = async (templates) => {
    const settings = { ...calendarSettings };
    settings.templates = templates;
    const response = await updateSettings(settings);
    return response;
  };

  const addTemplate = async (name, start, end) => {
    const templates = calendarSettings?.templates || [];
    const newId = (maxBy(templates, (x) => x.id)?.id || 0) + 1;
    await updateTemplates([...templates, { id: newId, name, start, end }]);
    setSelectedTemplate(newId);
  };

  const editTemplate = async (tempID, name, start, end) => {
    const templates = calendarSettings?.templates || [];
    if (
      (tempID === 0 || tempID === 1) &&
      !templates.find((x) => x.id === tempID)
    )
      await addTemplate(name, start);
    else
      await updateTemplates(
        templates.map((x) => {
          if (x.id !== tempID) return x;
          return { id: x.id, name, start, end };
        })
      );
  };

  const handleFutureTemplateSync = async (templateId) => {
    //  clear current template
    await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/clear/templates`,
      "POST"
    );

    // convert future template to current template
    await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/update/templates`,
      "POST",
      {
        from: templateId,
        to: 0,
      }
    );

    // remove multiple template from settings
    await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/settings`,
      "POST",
      { ...calendarSettings, multipleTemplate: false, templates: undefined }
    );

    await fetchSettings();
    await getDynamicSchedules();
  };

  const fetchHolidays = async () => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/holidays/fetch`,
      "GET"
    );
    if (response?.data?.holidays) setHolidays(response.data.holidays);
    return response;
  };

  useEffect(async () => {
    if (firstRenderRef.current && !allData?.length) await getDynamicSchedules();
    else firstRenderRef.current = true;
  }, [selectedDate]);

  useEffect(() => {
    setPublishedEvents(showPublished());
  }, [allData, mode, employeeData, periodicSchedule]);

  useEffect(() => {
    handleTemplateChange(selectedTemplate);
  }, [selectedTemplate]);

  // useEffect(() => {
  //     console.log('why this is loading for long time')
  //     // getEmployeeData()
  //     getDynamicSchedules();
  // },[])

  useEffect(async () => {
    if (mode === "edit") await addBatch(selectedDate);
  }, [selectedDate]);

  return {
    fetchLoading,
    mode,
    setMode,
    selectedDate,
    setSelectedDate,
    period,
    setPeriod,
    calendarSettings,
    defaultSchedules: defaultSchedulesWithHours,
    publishedEvents,
    resources,
    addNewEvent,
    getDynamicSchedules,
    getEmployeeData,
    employeeData,
    setRefetch,
    refetch,
    publishSchedules,
    deleteEvent,
    publishDraft,
    addBatch,
    getHistory,
    clearDraft,
    clearDraftAndCopyFromTemplate,
    clearTemplate,
    deleteStaffSchedule,
    republishStaffSchedule,
    assignStaffColor,
    draftAvailable,
    updateHolidays,
    fetchHolidays,
    fetchSettings,
    updateSettings,
    updateTemplates,
    addTemplate,
    editTemplate,
    setNDays,
    holidays,
    handleTemplateChange,
    selectedTemplate,
    setSelectedTemplate,
    getEffectiveTemplate,
    handleFutureTemplateSync,
    dynamicSchedules,
    staticSchedules,
    getPastSchedule,
    periodicSchedule,
    hasUnsavedDraft,
    setHasUnsavedDraft,
    updateEmployeeAppointmentVisibility,
  };
};

export default useScheduleData;
